import { useEffect } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import FullPageLoader from "@/ui/fullPageLoader/FullPageLoader";
import CourseLanding from "./CourseLanding";
import { shouldResume } from "./courseRedirect";

import { useGetCourse } from "@/features/courses/hooks/useGetCourse";
import { useGetCourseUnits } from "@/features/courses/hooks/useGetCourseUnits";
import { useUser } from "@/features/auth/hooks/useUser";

export function Course() {
	const { courseSlug } = useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const entry = useGetCourse({ courseSlug });
	const units = useGetCourseUnits({ courseSlug });
	const { isLoggedIn, isPending: userPending } = useUser();

	const entryUnit = entry.data?.video || entry.data?.quiz;
	const resumeUrl = entryUnit
		? `/courses/${courseSlug}/${entryUnit.module?.slug}/${entryUnit.slug}`
		: null;

	const completedUnits = units.course?.completed_units ?? 0;
	const resume = !userPending && resumeUrl && shouldResume({ isLoggedIn });

	useEffect(() => {
		if (!resume) return;

		// /courses/:slug/ returns the same shape as /courses/:slug/:module/:unit/,
		// so the entry payload is exactly what useGetUnit is about to fetch.
		// Priming its cache entry under the same key lets the player render
		// immediately instead of showing a loader through a redundant round-trip
		// on the hottest path in the product.
		queryClient.setQueryData(
			[
				"courses",
				courseSlug,
				"modules",
				entryUnit.module?.slug,
				"units",
				entryUnit.slug,
			],
			entry.data
		);

		navigate(resumeUrl, { replace: true });
	}, [
		resume,
		resumeUrl,
		navigate,
		queryClient,
		courseSlug,
		entryUnit,
		entry.data,
	]);

	// Some courses' first unit isn't a free preview, so /courses/:slug/ (the
	// `entry` query) 403s for anonymous visitors even though /units/ (the
	// `units` query) succeeds. A crawler still sees the landing page in that
	// case, so a real visitor should too — only a failed `units` query means
	// there is nothing to render.
	if (units.isError) return <Navigate to="/" />;

	// `entry.isPending` alone would never resolve once `entry` has settled
	// into an error, but check `entry.isError` explicitly anyway so a failed
	// entry never leaves the page stuck on the loader.
	const entryPending = entry.isPending && !entry.isError;

	if (entryPending || units.isPending || userPending || resume) {
		return <FullPageLoader />;
	}

	const firstModule = units.modules?.[0];
	const firstUnit = firstModule?.units?.[0];
	const firstUnitUrl = firstUnit
		? `/courses/${courseSlug}/${firstModule.slug}/${firstUnit.slug}`
		: resumeUrl;

	// The full course object — description, lecturers, difficulty, categories —
	// is the top-level `course` key on /courses/:slug/. The one nested under
	// video.module.course is a reduced copy without lecturers, and passing that
	// to AboutCourseSection would crash on `course.lecturers.map`. When `entry`
	// failed (403 on a non-preview course), we fall back to just `units.course`
	// plus the defaults below — no description, no lecturer bios.
	return (
		<CourseLanding
			course={{
				lecturers: [],
				categories: [],
				...units.course,
				...(entry.data?.course ?? {}),
			}}
			modules={units.modules ?? []}
			firstUnitUrl={firstUnitUrl}
			resumeUrl={resumeUrl}
			// Gated on resumeUrl: `entry` can fail for reasons other than a 403
			// (the landing page still renders), and hasProgress alone would then
			// make CourseLanding render <Link to={null}>.
			hasProgress={completedUnits > 0 && Boolean(resumeUrl)}
		/>
	);
}
